import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { addEntity, removeEntity, setAllEntities, withEntities } from '@ngrx/signals/entities';

import { Subject } from 'rxjs';

import { Constants } from 'app/constants/constants';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import {
  WidgetAnalysisDataSource,
  WidgetAnalysisGroupDataSource,
  WidgetKeyDriverAnalysisDataSource,
} from 'app/modules/widget/components/widget-data-source.component';
import { AnalysisGroup } from 'app/providers/storage-api/analysis-group-service';
import { Analysis, AnalysisService } from 'app/providers/storage-api/analysis.service';
import { CollectionName, CollectionService } from 'app/providers/storage-api/collection.service';
import { ContentChannelService } from 'app/providers/storage-api/content-channel.service';
import {
  CalculationListItem,
  PredictiveService,
} from 'app/providers/storage-api/predictive.service';
import { Project, ProjectService } from 'app/providers/storage-api/project.service';
import { LoggerService } from 'app/services/logger.service';

import { WidgetDataSource } from '../../../models';
import { DashboardDatasourceFilterService } from '../services/dashboard-datasource-filter.service';

export const DATASOURCE_GROUP_KEY = Constants.DATASOURCE_GROUP_KEY;

export interface CollectionNameMapType {
  [key: string]: CollectionName;
}

export interface DatasourcesType {
  [key: string]: WidgetDataSource[];
}

export interface DatasourcesByIdType {
  [id: string]: WidgetDataSource;
}

interface DatasourceManagerState {
  collectionNameMap: CollectionNameMapType;
  contentChannelPaths: Record<string, string>;
  projectId: string | undefined;
  dataSourceWidgetMap: Record<string, string[]>;
  analyses: Analysis[];
  dashboardId: string | undefined;
  project: Project | undefined;
}

const initialState: DatasourceManagerState = {
  collectionNameMap: {},
  contentChannelPaths: {},
  projectId: undefined,
  dataSourceWidgetMap: {},
  analyses: [],
  dashboardId: undefined,
  project: undefined,
};

export const DatasourceManagerStore = signalStore(
  withState(initialState),
  withEntities<WidgetDataSource>(),
  withProps(() => ({
    widgetMapChange$: new Subject<{
      action: 'ADD' | 'REMOVE';
      dataSourceId: string;
      widgetId: string;
    }>(),
  })),
  withComputed((state) => ({
    dataSourcesByCollection: computed((): DatasourcesType => {
      const map: DatasourcesType = {};
      for (const ds of state.entities()) {
        const key = ds.groupId ? Constants.DATASOURCE_GROUP_KEY : ds.collectionName;
        map[key] = map[key] ?? [];
        map[key].push(ds);
      }
      return map;
    }),
    hasData: computed(() => state.entities().some((ds) => !ds.groupId)),
  })),
  withMethods(
    (
      store,
      predictiveService = inject(PredictiveService),
      projectService = inject(ProjectService),
      analysisService = inject(AnalysisService),
      customFieldsService = inject(CustomFieldsService),
      contentChannelService = inject(ContentChannelService),
      logger = inject(LoggerService, { optional: true }),
      dashboardDataSourceFilterService = inject(DashboardDatasourceFilterService)
    ) => {
      function isUsableAnalysis(analysis: Analysis): boolean {
        const col = store.collectionNameMap()[analysis.collectionName];
        const isAutoAnalyzeInProgress =
          !!col?.isAutoAnalyze &&
          (analysis.status === 'OPEN' ||
            analysis.status === 'QUEUED' ||
            analysis.status === 'IDLE');
        return isAutoAnalyzeInProgress || analysis.isReAnalyze || analysis.status === 'IDLE';
      }

      async function registerDataSource(dataSource: WidgetDataSource): Promise<WidgetDataSource> {
        dataSource.unAliasedCustomFields = (dataSource.customFields || []).map((item) =>
          item.slice()
        );
        dataSource.customFields = await customFieldsService.getMetadataSubTypesAsync(dataSource);
        dataSource.unAliasedWeightFields =
          await customFieldsService.getUnAliasedWeightFieldsAsync(dataSource);
        if (store.entityMap()[dataSource.id]) {
          patchState(store, removeEntity(dataSource.id));
        }
        patchState(store, addEntity(dataSource));
        return dataSource;
      }

      async function registerKeyDriverAnalysis(
        calculation: CalculationListItem,
        collectionName: string
      ): Promise<WidgetKeyDriverAnalysisDataSource> {
        const dataSource = new WidgetKeyDriverAnalysisDataSource(calculation);
        const colName = store.collectionNameMap()[calculation.collectionId];
        dataSource.collectionDisplayName = colName?.name ?? collectionName;
        dataSource.isAutoAnalyze = false;
        dataSource.isDriverAnalysis = true;
        dataSource.contentChannel = colName?.contentChannel;
        await registerDataSource(dataSource);
        return dataSource;
      }

      async function registerCollection(colName: CollectionName): Promise<void> {
        patchState(store, {
          collectionNameMap: { ...store.collectionNameMap(), [colName.id]: colName },
        });
        if (colName.contentChannel && !(colName.contentChannel in store.contentChannelPaths())) {
          const ccparentTree = await contentChannelService.getContentChannelParentTree(
            colName.contentChannel
          );
          patchState(store, {
            contentChannelPaths: {
              ...store.contentChannelPaths(),
              [colName.contentChannel]: ccparentTree.map((cc) => cc.label).join('/'),
            },
          });
        }
        if (colName.isDriverAnalysis) {
          const resp = await predictiveService.listCalculations(store.projectId(), colName.id, {
            tags: ['fromWidget'],
            pageSize: 1024,
          });
          for (const calculation of resp.items) {
            if (calculation.tags?.length) {
              await registerKeyDriverAnalysis(calculation, colName.name);
            }
          }
        }
      }

      async function registerGroup(group: AnalysisGroup): Promise<WidgetAnalysisGroupDataSource> {
        const ds = new WidgetAnalysisGroupDataSource(group);
        if (ds.filterLookupId) {
          ds.unfilteredDocumentCount = ds.documentCount;
          const filterCounts = await analysisService.getAnalysisDocumentCount(
            store.projectId(),
            ds.filterLookupId
          );
          ds.documentCount = filterCounts.documentCounts
            .filter((entry) => ds.groupAnalyses.includes(entry.value))
            .reduce((prev, curr) => prev + curr.count, 0);
        }
        ds.calculatedFields = [
          ...new Set(
            ds.groupCollections.flatMap((c) => store.collectionNameMap()[c]?.calculatedFields)
          ),
        ];
        await registerDataSource(ds);
        return ds;
      }

      async function registerAnalysis(
        analysis: Analysis
      ): Promise<WidgetAnalysisDataSource | null> {
        if (!isUsableAnalysis(analysis)) {
          patchState(store, removeEntity(analysis.name));
          return null;
        }
        const dataSource = new WidgetAnalysisDataSource(analysis);
        const col = store.collectionNameMap()[dataSource.collectionName];
        if (!col && analysis.status !== 'DELETING') {
          logger?.warn('OrphanAnalysis', {
            analysisId: analysis.name,
            collectionName: analysis.collectionName,
          });
          return null;
        }
        dataSource.collectionDisplayName = col.name ?? dataSource.displayName;
        dataSource.isAutoAnalyze = col.isAutoAnalyze;
        const collectionDatasources = store
          .entities()
          .filter((ds) => !ds.groupId && ds.collectionName === col.id);
        dataSource.isDriverAnalysis =
          col.isDriverAnalysis && collectionDatasources.every((ds) => !ds.isDriverAnalysis);
        dataSource.hasSections = col.hasSections;
        dataSource.sectionFieldNames = col.sectionFieldNames;
        dataSource.calculatedFields = col.calculatedFields;
        dataSource.contentChannel = col.contentChannel;
        await registerDataSource(dataSource);
        const kdas = store
          .entities()
          .filter(
            (ds) => ds.collectionName === col.id && ds instanceof WidgetKeyDriverAnalysisDataSource
          );
        for (const kda of kdas) {
          kda.unAliasedCustomFields = dataSource.unAliasedCustomFields;
          kda.unAliasedWeightFields = dataSource.unAliasedWeightFields;
          kda.customFields = dataSource.customFields;
        }
        return dataSource;
      }

      async function registerDataSources(
        collections: CollectionName[],
        analysisGroups: AnalysisGroup[] = []
      ): Promise<void> {
        patchState(store, setAllEntities<WidgetDataSource>([]));
        patchState(store, { collectionNameMap: {} });
        for (const collection of collections ?? []) {
          await registerCollection(collection);
        }
        for (const group of analysisGroups ?? []) {
          await registerGroup(group);
        }
        for (const analysis of store.analyses()) {
          await registerAnalysis(analysis);
        }
        const dashboardId = store.dashboardId();
        const project = store.project();
        if (!dashboardId || !project) {
          return;
        }
        const dashboard = project.dashboards?.find((db) => db.id === dashboardId);
        if (dashboard) {
          const filteredMap = dashboardDataSourceFilterService.filterDataSourcesMap(
            store.dataSourcesByCollection(),
            dashboard.payload.dataSourceFilters
          ) as DatasourcesType;
          const allowedIds = new Set(
            Object.values(filteredMap).flatMap((dsList) => dsList.map((ds) => ds.id))
          );
          patchState(
            store,
            setAllEntities<WidgetDataSource>(store.entities().filter((ds) => allowedIds.has(ds.id)))
          );
        }
      }

      function registerDataSourceToWidget(
        widgetId: string,
        dataSourceId: string,
        shouldTriggerMapChange = true
      ): void {
        const current = store.dataSourceWidgetMap();
        if (!current[dataSourceId]) {
          patchState(store, { dataSourceWidgetMap: { ...current, [dataSourceId]: [widgetId] } });
          return;
        }
        const existing = current[dataSourceId];
        if (!existing.includes(widgetId)) {
          patchState(store, {
            dataSourceWidgetMap: {
              ...current,
              [dataSourceId]: Array.from(new Set([...existing, widgetId])),
            },
          });
          if (shouldTriggerMapChange) {
            store.widgetMapChange$.next({ action: 'ADD', dataSourceId, widgetId });
          }
        }
      }

      function removeDataSourceFromWidgetMap(widgetId: string, dataSourceId: string): void {
        const current = store.dataSourceWidgetMap();
        if (!current[dataSourceId]) {
          return;
        }
        const existing = current[dataSourceId];
        if (!existing?.length) {
          const next = { ...current };
          delete next[dataSourceId];
          patchState(store, { dataSourceWidgetMap: next });
          return;
        }
        const filtered = existing.filter((w) => w !== widgetId);
        const next = { ...current, [dataSourceId]: Array.from(new Set(filtered)) };
        patchState(store, {
          dataSourceWidgetMap: next,
        });
        store.widgetMapChange$.next({ action: 'REMOVE', dataSourceId, widgetId });
      }

      return {
        initialize(params: {
          analyses: Analysis[];
          dashboardId?: string;
          project?: Project;
        }): void {
          patchState(store, {
            analyses: params.analyses ?? [],
            dashboardId: params.dashboardId,
            project: params.project,
          });
        },
        async loadDataSources(projectId: string): Promise<void> {
          patchState(store, { projectId });
          const sources = await projectService.getProjectSources(
            projectId,
            'collections',
            'analysisGroups'
          );
          await registerDataSources(sources.collections?.items, sources.analysisGroups?.items);
        },
        async loadDataSourcesFromProject(project: Project): Promise<void> {
          patchState(store, { projectId: project.id });
          const collectionNames = project.collections?.map(
            CollectionService.mapCollectionToCollectionName
          );
          await registerDataSources(collectionNames, project.groups);
        },
        registerCollection,
        registerKeyDriverAnalysis,
        registerAnalysis,
        registerGroup,
        registerDataSource,
        isUsableAnalysis,
        getById(id: string): WidgetDataSource | undefined {
          return store.entityMap()[id];
        },
        getByCollectionName(collectionName: string): WidgetDataSource[] {
          return store.dataSourcesByCollection()[collectionName] ?? [];
        },
        getDatasources(): DatasourcesType {
          return store.dataSourcesByCollection();
        },
        getAnalyses(): Analysis[] {
          return store.analyses();
        },
        removeDataSource(dataSource: WidgetDataSource): void {
          patchState(store, removeEntity(dataSource.id));
        },
        removeAnalysis(analysis: Analysis): void {
          patchState(store, removeEntity(analysis.name));
        },
        removeAnalysisByCollectionAndAnalysisId(_collectionId: string, analysisName: string): void {
          patchState(store, removeEntity(analysisName));
        },
        removeDriverAnalysis(driverAnalysis: CalculationListItem): void {
          patchState(store, removeEntity(driverAnalysis.id));
        },
        removeDriverAnalysisByIdAndCollectionId(id: string, _collectionId: string): void {
          patchState(store, removeEntity(id));
        },
        removeGroup(group: AnalysisGroup): void {
          patchState(store, removeEntity(group.id));
        },
        removeGroupById(groupId: string): void {
          patchState(store, removeEntity(groupId));
        },
        removeByCollection(collectionName: string): void {
          const remaining = store
            .entities()
            .filter((ds) => ds.groupId || ds.collectionName !== collectionName);
          patchState(store, setAllEntities<WidgetDataSource>(remaining));
        },
        getActiveDataSources(dataSourceIds: string[]): WidgetDataSource[] {
          return dataSourceIds.map(
            (id) =>
              store.entityMap()[id] ?? ({ id, isDeleted: true } as unknown as WidgetDataSource)
          );
        },
        registerDataSourceToWidget,
        removeDataSourceFromWidgetMap,
        changeDataSource(widgetId: string, nextId: string | undefined, prevId?: string): void {
          if (nextId) {
            registerDataSourceToWidget(widgetId, nextId);
          }
          if (prevId) {
            removeDataSourceFromWidgetMap(widgetId, prevId);
          }
        },
        clearDataSourceWidgetMap(): void {
          patchState(store, { dataSourceWidgetMap: {} });
          this.widgetMapChange$.next(undefined);
        },
        getFullContentChannel(id: string): string | undefined {
          return store.contentChannelPaths()[id];
        },
      };
    }
  )
);
